import { Check } from "lucide-react";
import { Reveal } from "@/components/site/Motion";
import { Label, Section, H2, Paras, BookMeeting, CtaButton, MetricsCloud } from "@/components/site/Blocks";

const QUESTIONS = ["What your business does", "Which topics you are relevant to", "Which markets you operate in", "Whether your information is trustworthy", "How your website relates to other sources across the web", "Whether your business deserves to be surfaced for a particular search or recommendation"];

export const Expertise = () => (
  <Section tone="subtle" testid="expertise-section">
    <div className="grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n="05">Expertise</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">Search Visibility Requires Specialist Expertise</H2></Reveal>
        <Reveal delay={0.1}><Paras className="mt-6" items={["Modern SEO is considerably more technical than simply adding keywords to pages. And becoming visible across AI-powered search introduces another layer of complexity."]} /></Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_40px_80px_-50px_rgba(30,27,75,0.35)]">
          <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" /><span className="h-2.5 w-2.5 rounded-full bg-slate-300" /><span className="h-2.5 w-2.5 rounded-full bg-violet-400" />
            <span className="ml-3 font-mono text-[11px] text-slate-500">Search engines and AI systems need to determine:</span>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="expertise-questions">
            {QUESTIONS.map((q, i) => (
              <Reveal as="li" key={q} delay={i * 0.06} y={10} className="flex gap-4 px-5 py-4 font-mono text-[13px] text-slate-800 sm:px-7 sm:text-sm">
                <span className="text-violet-600">&gt;</span>
                <span className={i === QUESTIONS.length - 1 ? "caret" : ""}>{q}</span>
              </Reveal>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}><p className="mt-8 font-display text-xl leading-snug text-slate-900 sm:text-2xl">Perfect Digitals combines technical SEO, content strategy, structured information, authority development and AI Search Optimisation to address the complete search ecosystem.</p></Reveal>
      </div>
    </div>
  </Section>
);

export const Results = () => (
  <MetricsCloud
    n="06" label="Results" testid="results-section"
    title={<>Results, Not Just <span className="font-serif-i text-gradient">Rankings</span></>}
    intro={["Search visibility ultimately needs to contribute to business growth.", "That is why we look beyond individual keyword positions. Depending on the campaign, we measure:"]}
    items={["Organic visibility", "Commercial keyword performance", "Organic traffic", "Enquiries and conversions", "Search visibility against competitors", "AI mentions and recommendations", "AI citations", "Content performance", "Technical improvements"]}
    outro={<>Our goal is not simply to make graphs move upwards. It is to make your business easier for potential customers to discover.<span className="mt-8 block"><CtaButton label="View Our Results" to="/results" testid="view-results-cta" /></span></>}
  />
);

const SCAN = [["Google", ["Current SEO position", "Technical foundations", "Important competitors", "Search opportunities"]], ["AI Search", ["Brand representation", "Relevant AI platforms", "Competitor representation", "Visibility gaps"]]];

export const AuditCallout = () => (
  <Section testid="audit-section">
    <div className="grid items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Reveal><Label n="08">Visibility audit</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">How Visible Is Your Business Across <span className="font-serif-i text-gradient-dark">Google and AI?</span></H2></Reveal>
        <Reveal delay={0.1}><Paras className="mt-6" items={[
          "Most businesses have some understanding of how they perform on Google. Very few understand how visible they are across the emerging AI search ecosystem.",
          "Our Search Visibility Audit examines both.",
          "We analyse your current SEO position, technical foundations, important competitors and search opportunities alongside how your brand is being represented across relevant AI search platforms.",
          "The result is a clearer picture of where you are visible today and what needs to improve next.",
        ]} /></Reveal>
        <Reveal delay={0.15} className="mt-10"><BookMeeting variant="dark" testid="audit-section-book-meeting" /></Reveal>
      </div>
      <Reveal delay={0.1} className="relative overflow-hidden rounded-[28px] bg-ink p-6 text-white sm:p-8 lg:col-span-6" data-testid="audit-scan-panel">
        <div className="grid-lines absolute inset-0 opacity-70" />
        <div className="scan-bar absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-transparent via-violet-500/25 to-transparent" />
        <div className="relative flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-violet-300">
          <span>Search Visibility Audit</span><span className="flex items-center gap-2"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />Scanning</span>
        </div>
        <div className="relative mt-8 grid gap-4 sm:grid-cols-2">
          {SCAN.map(([k, rows]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md">
              <p className="font-display text-xl">{k}</p>
              <ul className="mt-4 space-y-3">
                {rows.map((r) => (
                  <li key={r} className="flex items-center gap-3 text-[13.5px] text-slate-300">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-500/25 text-violet-200"><Check className="h-3 w-3" /></span>{r}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </Section>
);
